/**
 * Turning a word sequence into lines, and later into cues.
 *
 * Two rules do the work here, and both exist because Russian reads badly when
 * broken carelessly. A line may not end on a word that belongs to the next
 * one, and where a comma is available it is the better break, because it is
 * already a pause.
 */

import { endsWithAny, len, rstrip, split, strip, sum } from "./py";
import { type Pixels, px } from "./units";

export const MAX_CAPTION_LINES = 2;

export const FUNCTION_WORDS: ReadonlySet<string> = new Set(
  split(`
    в и на с со при не что это а но да за из к по о от до для над под без
    у же ли бы или к ко во об обо через про между
    `),
);

/**
 * Russian places the adjective before its noun, so breaking after one splits a
 * phrase that must be read together. Agreement endings are signal enough.
 */
export const ADJECTIVE_ENDINGS: readonly string[] = [
  "ый",
  "ий",
  "ой",
  "ая",
  "яя",
  "ые",
  "ие",
  "ую",
  "юю",
  "ых",
  "их",
  "ому",
  "ему",
  "ым",
  "им",
];

const PUNCTUATION = ".,!?—:;«»\"'()";
const SENTENCE_FINAL = [".", "!", "?", "…"];
const TRAILING = "»\"')";
const DANGLING_PENALTY = 100;
const COMMA_BONUS = 12;

export type Measure = (text: string) => number;

/** A word with its punctuation removed, lowercased, for matching. */
export function bare(word: string): string {
  return strip(word, PUNCTUATION).toLowerCase();
}

/** True when the word carries sentence-final punctuation. */
export function endsSentence(word: string): boolean {
  return endsWithAny(rstrip(word, TRAILING), SENTENCE_FINAL);
}

/**
 * True when a line or cue must not end on this word. A word that ends a
 * sentence is never dangling, whatever its ending: there is no following noun
 * across a full stop.
 */
export function isDangling(word: string): boolean {
  const stripped = bare(word);
  if (!stripped) return false;
  if (endsSentence(word)) return false;
  if (FUNCTION_WORDS.has(stripped)) return true;
  return endsWithAny(stripped, ADJECTIVE_ENDINGS);
}

/** The default measure: one unit per character, as a monospace face sets. */
export const measureCharacters: Measure = (text) => len(text);

function width(line: readonly string[], measure: Measure): number {
  return sum(line.map(measure)) + Math.max(line.length - 1, 0) * measure(" ");
}

function fits(line: readonly string[], budget: number, measure: Measure): boolean {
  return width(line, measure) <= budget;
}

/** Fill lines left to right. Used only when no split satisfies the budget. */
function greedy(words: readonly string[], budget: number, maxLines: number, measure: Measure): string[][] {
  const lines: string[][] = [];
  let current: string[] = [];
  for (const word of words) {
    if (current.length && !fits([...current, word], budget, measure) && lines.length < maxLines - 1) {
      lines.push(current);
      current = [word];
    } else {
      current.push(word);
    }
  }
  if (current.length) lines.push(current);
  return lines;
}

/** Every way of cutting `count` words into at most `maxLines` runs, in Python's order. */
function splits(count: number, maxLines: number): number[][] {
  if (maxLines <= 1 || count <= 1) return [[]];
  const out: number[][] = [[]];
  for (let cut = 1; cut < count; cut++) {
    for (const rest of splits(count - cut, maxLines - 1)) out.push([cut, ...rest.map((r) => cut + r)]);
  }
  return out;
}

function cost(lines: readonly string[][], balanceWeight: number, measure: Measure): number {
  const widths = lines.map((line) => width(line, measure));
  let total = (Math.max(...widths) - Math.min(...widths)) * balanceWeight;
  for (const line of lines.slice(0, -1)) {
    const last = line[line.length - 1] ?? "";
    if (isDangling(last)) total += DANGLING_PENALTY;
    if (last.endsWith(",")) total -= COMMA_BONUS;
  }
  return total;
}

/**
 * Break a cue into lines, obeying the budget and both reading rules. Every
 * arrangement that fits is scored, rather than filling greedily: a greedy fill
 * commits to a break before it can know a later one would have avoided ending
 * on a preposition.
 */
export function breakLines(
  words: readonly string[],
  budget: number,
  maxLines: number = MAX_CAPTION_LINES,
  balanceWeight = 1.0,
  measure: Measure = measureCharacters,
): string[][] {
  if (!words.length) return [];
  let best: { cost: number; lines: string[][] } | null = null;
  for (const cuts of splits(words.length, maxLines)) {
    const bounds = [0, ...cuts, words.length];
    const lines: string[][] = [];
    for (let index = 0; index + 1 < bounds.length; index++) {
      const a = bounds[index] ?? 0;
      const b = bounds[index + 1] ?? 0;
      if (b > a) lines.push(words.slice(a, b));
    }
    if (!lines.every((line) => fits(line, budget, measure))) continue;
    const score = cost(lines, balanceWeight, measure);
    if (best === null || score < best.cost) best = { cost: score, lines };
  }
  // A single word longer than the budget, most likely: emit it anyway.
  return best === null ? greedy(words, budget, maxLines, measure) : best.lines;
}

/** Measured width of a line, in source pixels, with the given gap between words. */
export function lineWidth(line: readonly string[], measure: Measure, wordGap: Pixels): Pixels {
  return px(sum(line.map(measure)) + Math.max(line.length - 1, 0) * wordGap);
}
